import type {
  HealthCheckFinding,
  HealthCheckInternalRouteTarget,
} from '../models/healthCheck.types'
import { UnsafeActionTargetError } from '../service/actionUrl'

import type {
  HealthCheckActionApi,
  HealthCheckActionStore,
  HealthCheckNotifier,
} from './healthCheckFlow.types'

import { ApiError } from '@/shared/http'

export interface RunFindingActionFlowDeps {
  store: HealthCheckActionStore
  api: HealthCheckActionApi
  notifier: HealthCheckNotifier
  openExternal: (target: string) => void
  navigateInternal: (target: HealthCheckInternalRouteTarget) => Promise<void> | void
  reload: () => Promise<void>
}

export class RunFindingActionFlow {
  constructor(private readonly deps: RunFindingActionFlowDeps) {}

  async execute(finding: HealthCheckFinding): Promise<void> {
    const action = finding.action
    if (!action || this.deps.store.actionPending[finding.id]) return

    if (action.kind === 'externalLink') {
      this.deps.openExternal(action.target)
      return
    }
    if (action.kind === 'internalRoute') {
      await this.deps.navigateInternal(action.target)
      return
    }
    if (action.kind === 'unsafe') {
      this.deps.notifier.error(new UnsafeActionTargetError(action.target).i18nKey)
      return
    }

    this.deps.store.setActionPending(finding.id, true)
    try {
      const result = await this.deps.api.runAction(action.target)
      if (result.message) {
        this.deps.notifier.success('healthChecks.action.success', { detail: result.message })
      }
      await this.deps.reload()
    } catch (cause) {
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (error.kind === 'canceled') return
      if (error.kind === 'notFound') {
        this.deps.notifier.error('healthChecks.triage.stale')
        await this.deps.reload()
        return
      }
      this.deps.notifier.error('healthChecks.action.failed', { detail: error.detail })
    } finally {
      this.deps.store.setActionPending(finding.id, false)
    }
  }
}
